/**
 * Claro, escuro, ou o que o aparelho mandar.
 *
 *   «coloque a opção de dark mode ou bright mode, igual ao labmanager»
 *                                              — Testesolo, 11/09/2026
 *
 * ── Três estados, e o terceiro é o padrão ────────────────────────────
 *
 * Claro, escuro e «o que o sistema mandar». O terceiro é o padrão de
 * propósito: quem configurou o celular para escurecer às seis da tarde
 * não deveria ter que configurar de novo aqui. Só quem discorda do
 * aparelho é que escolhe — e essa escolha ganha do aparelho.
 *
 * ── Por que `data-theme` e não `data-tema` ───────────────────────────
 *
 * Porque é o nome que o arquivo de tokens compartilhado já atende para o
 * portal. A bancada carimba `data-tema="claro|escuro"`; o portal,
 * `data-theme="light|dark"`. São duas aplicações que nasceram em meses
 * diferentes e o arquivo de tokens da 176 aceita as duas grafias, lado a
 * lado, justamente para o botão das duas funcionar. Unificar o nome é
 * trabalho de uma onda que renomeie os dois ao mesmo tempo; até lá,
 * seguir o que a folha já espera custa nada e evita um botão morto.
 *
 * ── O que o estado «sistema» NÃO faz ─────────────────────────────────
 *
 * Não carimba atributo nenhum. É o que deixa a `@media
 * (prefers-color-scheme)` decidir. Carimbar `light` ao abrir — que é o
 * erro fácil — faria o portal ignorar o aparelho de todo mundo que nunca
 * tocou no botão.
 */

export type Tema = 'claro' | 'escuro' | 'sistema';

const CHAVE = 'testesolo.portal.tema';

export function lerTema(): Tema {
  try {
    const t = localStorage.getItem(CHAVE);
    if (t === 'claro' || t === 'escuro') return t;
  } catch { /* janela anônima, cota, navegador que recusa: fica no sistema */ }
  return 'sistema';
}

export function aplicarTema(t: Tema): void {
  const raiz = document.documentElement;
  if (t === 'sistema') raiz.removeAttribute('data-theme');
  else raiz.setAttribute('data-theme', t === 'escuro' ? 'dark' : 'light');
  try {
    if (t === 'sistema') localStorage.removeItem(CHAVE);
    else localStorage.setItem(CHAVE, t);
  } catch { /* idem: sem memória, o tema vale só para esta visita */ }
}

/** O próximo da roda: sistema → claro → escuro → sistema. */
export function proximoTema(t: Tema): Tema {
  return t === 'sistema' ? 'claro' : t === 'claro' ? 'escuro' : 'sistema';
}

export const NOME_TEMA: Record<Tema, string> = {
  sistema: 'Tema do aparelho',
  claro: 'Tema claro',
  escuro: 'Tema escuro',
};

/**
 * O desenho de cada estado.
 *
 * Sol, lua e meia-lua — e o `title` diz o nome por extenso, porque três
 * ícones parecidos num canto de tela não se explicam sozinhos.
 */
export const ICONE_TEMA: Record<Tema, string> = {
  claro: 'M12 4V2m0 20v-2m8-8h2M2 12h2m13.66-5.66l1.41-1.41M4.93 19.07l1.41-1.41'
       + 'm11.32 0l1.41 1.41M4.93 4.93l1.41 1.41M16 12a4 4 0 11-8 0 4 4 0 018 0z',
  escuro: 'M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z',
  sistema: 'M12 3a9 9 0 000 18zm0 0a9 9 0 010 18',
};

/*
 * ── O tema EFETIVO (onda 296, o motor do mapa) ──────────────────────
 *
 * A folha de estilo resolve «sistema» sozinha, pela media query. O mapa
 * não: o MapLibre desenha em WebGL, com um estilo claro OU escuro montado
 * em JavaScript (spec 05 §4.5), e precisa saber qual dos dois está valendo
 * — o atributo, se houver, senão o aparelho. É a mesma conta da bancada,
 * no atributo do portal: `data-theme="light|dark"` (ver acima).
 */
export type TemaEfetivo = 'claro' | 'escuro';

const ESCURO_NO_APARELHO = '(prefers-color-scheme: dark)';

export function temaEfetivo(): TemaEfetivo {
  const t = document.documentElement.getAttribute('data-theme');
  if (t === 'dark') return 'escuro';
  if (t === 'light') return 'claro';
  return typeof window.matchMedia === 'function' && window.matchMedia(ESCURO_NO_APARELHO).matches
    ? 'escuro' : 'claro';
}

/**
 * Avisa `fn` quando o tema efetivo muda: o botão de tema troca o
 * `data-theme`, ou o aparelho escurece às seis da tarde com o tema em
 * «sistema». Só avisa MUDANÇA (trocar «sistema» por «claro» num aparelho
 * claro não muda nada que se veja). Devolve a função que desliga o aviso.
 */
export function aoMudarTemaEfetivo(fn: (t: TemaEfetivo) => void): () => void {
  let atual = temaEfetivo();
  const conferir = () => {
    const novo = temaEfetivo();
    if (novo !== atual) { atual = novo; fn(novo); }
  };
  const observador = new MutationObserver(conferir);
  observador.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  const consulta = typeof window.matchMedia === 'function' ? window.matchMedia(ESCURO_NO_APARELHO) : null;
  consulta?.addEventListener('change', conferir);
  return () => {
    observador.disconnect();
    consulta?.removeEventListener('change', conferir);
  };
}
